import type { TemplatePrompts } from '@kimuno/api-sdk/lib/types/common/Conversation';
import { Box } from '@mui/material';
import config from 'config';
import { Link } from 'found';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getGeneratedArtifacts as getGeneratedArtifactsAction } from '../../../actions/aiAssistant.actions';
import {
	borderRadius,
	boxShadows,
	customSizes,
	spaces,
} from '../../../assets/styles/themes/tokens';
import { aiAssistantPageSource, numberOfSlides, routes } from '../../../constants';
import { AiLoadingStatus } from '../../../reducers/aiAssistant.reducers';
import {
	getArtifactsLoadingStatus,
	getGeneratedArtifactsData,
} from '../../../reducers/aiAssistant.selectors';
import { selectUser } from '../../../reducers/user.selectors';
import ArtifactCard from '../../../routes/MyContentsPage/ArtifactCard/ArtifactCard';
import { useAmplitudeExperiment } from '../../../utils/ampli';
import useAction from '../../../utils/useAction';
import useRouter from '../../../utils/useRouter';
import useShouldShowAIAssistant from '../../../utils/useShouldShowAIAssistant';
import AIFeatureCard from '../../AIAssistant/shared/TemplatePromptForm/AIFeatureCard';
import { getStartPageActionCards } from '../../AIAssistant/shared/utils';
import CardSkeleton from '../../Carousel/CardSkeleton';
import NewCarousel from '../../Carousel/NewCarousel';
import { getDateKey, getMukiCardLaunch } from './getMukiCardLaunch';

const EXISTING_USER_THRESHOLD = 4;
const EXISTING_USER_ARTIFACTS_COUNT = 8;
const CARD_WIDTH = 300;
const TEMPLATE_CARD_HEIGHT = 116;
const ARTIFACT_CARD_HEIGHT = Number.parseInt(customSizes.artifactCard.cardHeight, 10);

const MukiActionCarousel: React.FC = () => {
	const { t } = useTranslation();
	const { router } = useRouter();
	const user = useSelector(selectUser);
	const shouldShowAIAssistant = useShouldShowAIAssistant();
	const { isReady, variant } = useAmplitudeExperiment(user, config.amplitudeExperiments.dashboard);

	const getGeneratedArtifacts = useAction(getGeneratedArtifactsAction);
	const artifacts = useSelector(getGeneratedArtifactsData);
	const artifactsStatus = useSelector(getArtifactsLoadingStatus);

	const isVisible = isReady && variant === 'treatment' && shouldShowAIAssistant;

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		// Refetch on every dashboard entry so artifacts created or deleted in
		// KI-Tools are reflected here.
		if (isVisible) getGeneratedArtifacts();
	}, [isVisible]);

	// A batch-0 refetch briefly clears the store; keep the last non-empty list
	// (and the fact that we've loaded at least once) so an already-populated
	// section keeps showing its cards instead of collapsing or re-flashing the
	// skeleton while the refresh is in flight.
	const lastArtifactsRef = useRef(artifacts);
	if (artifacts.length > 0) lastArtifactsRef.current = artifacts;
	const hasLoadedOnceRef = useRef(false);
	if (artifactsStatus === AiLoadingStatus.LOADED) hasLoadedOnceRef.current = true;

	// While a refresh is in flight (store cleared), fall back to the last known
	// list; once the fetch settles as LOADED, trust the fresh result even if empty.
	const displayedArtifacts =
		artifacts.length > 0 || artifactsStatus === AiLoadingStatus.LOADED
			? artifacts
			: lastArtifactsRef.current;

	const dateKey = getDateKey();

	const templateCards = useMemo(() => {
		if (!user) return [];
		return getStartPageActionCards(user).map((item) => ({
			item,
			launch: getMukiCardLaunch(user, item.id as TemplatePrompts, dateKey),
		}));
	}, [user, dateKey]);

	if (!user || !isVisible) return null;

	const isExistingUser = displayedArtifacts.length >= EXISTING_USER_THRESHOLD;

	if (!hasLoadedOnceRef.current && displayedArtifacts.length === 0) {
		return (
			<NewCarousel title={' '} cardWidth={CARD_WIDTH} loading>
				{new Array(numberOfSlides.xl).fill(null).map((_, index) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: index keys are pre-existing; switching the key source changes reconciliation behavior
					<Box key={index} sx={{ paddingRight: spaces.md }}>
						<CardSkeleton height={ARTIFACT_CARD_HEIGHT} width={CARD_WIDTH} radius={borderRadius.md} />
					</Box>
				))}
			</NewCarousel>
		);
	}

	const recentArtifacts = isExistingUser
		? [...displayedArtifacts]
				.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
				.slice(0, EXISTING_USER_ARTIFACTS_COUNT)
		: [];

	const title = isExistingUser
		? t('What would you like to continue with?')
		: t('What would you like to create today?');

	return (
		<NewCarousel
			title={title}
			cardWidth={CARD_WIDTH}
			onShowAll={isExistingUser ? () => router.push(`/${routes.myContents.root}`) : undefined}
		>
			{recentArtifacts.map((artifact) => (
				<Box
					key={artifact.id}
					sx={{
						paddingRight: spaces.md,
						'& [data-testid="artifact-card"]': {
							borderRadius: borderRadius.md,
							boxShadow: boxShadows.elevation1,
						},
						'& .MuiCardHeader-title .MuiTypography-root': {
							fontSize: '14px',
							fontWeight: 700,
							lineHeight: '24px',
						},
					}}
				>
					<ArtifactCard artifact={artifact} pageSource={aiAssistantPageSource.dashboard} />
				</Box>
			))}
			{!isExistingUser &&
				templateCards.map(({ item, launch }) => (
					<Box
						key={item.id}
						sx={{ paddingRight: spaces.md, '& a': { textDecoration: 'none', display: 'block' } }}
					>
						<Link to={launch.to} data-action-id={item.id} data-testid={`muki-action-card-${item.id}`}>
							<AIFeatureCard
								title={item.title}
								icon={item.icon}
								description={item.subheader}
								chip={item.chip}
								emphasized
								heightPx={TEMPLATE_CARD_HEIGHT}
								onClick={() => undefined}
							/>
						</Link>
					</Box>
				))}
		</NewCarousel>
	);
};

export default MukiActionCarousel;
